import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { Redo2, Save, ShieldCheck, Undo2 } from 'lucide-react';
import { persistCurrentProject } from '../lib/importProject';
import { useProjectStore } from '../store/projectStore';
import { APP_URLS, NETWORK_MENUS, PROFILE_LINKS, safeWebLink } from '../config/header';

const menuId = (label: string) => label.toLowerCase();
const ids = NETWORK_MENUS.map(({ label }) => menuId(label));

/** PicPlayer's two-part brand, shared network navigation and account affordance. */
export function AppHeader() {
  const { project, mutate, undo, redo, past, future, engineLabel, setError } = useProjectStore();
  const [active, setActive] = useState<string | null>(null);
  const [tabStop, setTabStop] = useState(ids[0]);
  const triggers = useRef(new Map<string, HTMLButtonElement>());
  const popover = useRef<HTMLDivElement>(null);
  const pendingFocus = useRef<'first' | 'last' | null>(null);
  const search = useRef({ text: '', time: 0 });
  const hoverOpened = useRef(false);
  const items = () => Array.from(popover.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);

  const close = useCallback((restoreFocus = false) => {
    if (restoreFocus && active) triggers.current.get(active)?.focus({ preventScroll: true });
    setActive(null);
    search.current = { text: '', time: 0 };
  }, [active]);

  function focusTrigger(id: string) {
    if (id !== 'profile') setTabStop(id);
    const trigger = triggers.current.get(id);
    trigger?.focus({ preventScroll: true });
    trigger?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
  function open(id: string, focus: 'first' | 'last' | null = null) {
    hoverOpened.current = false;
    pendingFocus.current = focus;
    if (active === id && focus) {
      const choices = items();
      (focus === 'last' ? choices.at(-1) : choices[0])?.focus();
    }
    setActive(id);
  }

  const position = useCallback(() => {
    if (!active || !popover.current) return;
    const trigger = triggers.current.get(active);
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menu = popover.current;
    const top = rect.bottom + 3;
    menu.style.top = `${top}px`;
    menu.style.maxHeight = `${Math.max(0, window.innerHeight - top - 8)}px`;
    const width = menu.getBoundingClientRect().width;
    const preferred = active === 'profile' ? rect.right - width : rect.left;
    menu.style.left = `${Math.max(8, Math.min(preferred, window.innerWidth - width - 8))}px`;
  }, [active]);

  useLayoutEffect(() => {
    position();
    if (active && pendingFocus.current) {
      const choices = items();
      (pendingFocus.current === 'last' ? choices.at(-1) : choices[0])?.focus();
      pendingFocus.current = null;
    }
  }, [active, position]);

  useEffect(() => {
    if (!active) return;
    function outside(event: Event) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (popover.current?.contains(target) || [...triggers.current.values()].some((button) => button.contains(target))) return;
      close();
    }
    const onEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); close(true); }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('focusin', outside);
    document.addEventListener('keydown', onEscape);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    window.visualViewport?.addEventListener('resize', position);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('focusin', outside);
      document.removeEventListener('keydown', onEscape);
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
      window.visualViewport?.removeEventListener('resize', position);
    };
  }, [active, close, position]);

  function onKeys(event: KeyboardEvent<HTMLElement>, id: string, inMenu = false) {
    const { key } = event;
    if (key === 'Tab') { close(inMenu); return; }
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(true); return; }
    if (['ArrowLeft', 'ArrowRight'].includes(key) && id !== 'profile') {
      event.preventDefault(); event.stopPropagation();
      const next = ids[(ids.indexOf(id) + (key === 'ArrowRight' ? 1 : -1) + ids.length) % ids.length]!;
      focusTrigger(next);
      if (active) open(next, 'first');
      return;
    }
    if (['ArrowDown', 'ArrowUp'].includes(key)) {
      event.preventDefault(); event.stopPropagation();
      if (!inMenu) open(id, key === 'ArrowUp' ? 'last' : 'first');
      else {
        const choices = items();
        const index = choices.indexOf(document.activeElement as HTMLElement);
        choices[(index + (key === 'ArrowDown' ? 1 : -1) + choices.length) % choices.length]?.focus();
      }
      return;
    }
    if (key === 'Home' || key === 'End') {
      event.preventDefault(); event.stopPropagation();
      if (inMenu) (key === 'Home' ? items()[0] : items().at(-1))?.focus();
      else if (id !== 'profile') focusTrigger((key === 'Home' ? ids[0] : ids.at(-1))!);
      return;
    }
    if (inMenu && key.length === 1 && key !== ' ') {
      event.preventDefault(); event.stopPropagation();
      const now = Date.now();
      search.current.text = now - search.current.time < 700 ? search.current.text + key.toLowerCase() : key.toLowerCase();
      search.current.time = now;
      const choices = items();
      const index = choices.indexOf(document.activeElement as HTMLElement);
      const ordered = [...choices.slice(index + 1), ...choices.slice(0, index + 1)];
      const match = ordered.find((item) => item.textContent?.trim().toLowerCase().startsWith(search.current.text));
      (match ?? ordered.find((item) => item.textContent?.trim().toLowerCase().startsWith(key.toLowerCase())))?.focus();
    }
  }

  function triggerProps(id: string) {
    return {
      id: `header-trigger-${id}`,
      ref: (element: HTMLButtonElement | null) => { if (element) triggers.current.set(id, element); else triggers.current.delete(id); },
      'aria-haspopup': 'menu' as const,
      'aria-expanded': active === id,
      'aria-controls': active === id ? `header-menu-${id}` : undefined,
      onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => onKeys(event, id),
      onClick: (event: MouseEvent<HTMLButtonElement>) => {
        focusTrigger(id);
        if (active === id && !hoverOpened.current) close();
        else open(id, event.detail === 0 ? 'first' : null);
      },
    };
  }
  async function save() {
    try { await persistCurrentProject(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); }
  }
  const currentMenu = NETWORK_MENUS.find(({ label }) => menuId(label) === active);

  return <>
    <header className="topbar">
      <div className="brand" aria-label="NumberNode — Data Dashboard">
        <img className="brand-mark" src="/icon.svg" alt="" width="24" height="24" />
        <span className="brand-copy">
          <span className="brand-name"><strong>Number</strong><span>Node</span></span>
          <small>Data Dashboard</small>
        </span>
      </div>
      <input className="project-name" value={project.name} aria-label="Project name" onChange={(event) => mutate('Rename project', (draft) => { draft.name = event.target.value || 'Untitled analysis'; })} />
      <div className="top-actions">
        <div className="engine-pill"><span className="status-dot" />{engineLabel}</div>
        <div className="history-actions">
          <button type="button" className="icon-button" disabled={!past.length} onClick={undo} aria-label="Undo" title={past.at(-1)?.label}><Undo2 size={15} /></button>
          <button type="button" className="icon-button" disabled={!future.length} onClick={redo} aria-label="Redo"><Redo2 size={15} /></button>
          <button type="button" className="icon-button" onClick={() => void save()} aria-label="Save locally"><Save size={15} /></button>
        </div>
        <button type="button" className={`chip-button privacy-control ${project.settings.localOnly ? 'is-local' : 'is-remote'}`}
          title={project.settings.localOnly ? 'Remote sources and ad-manager requests are blocked. Your project stays on this device.' : 'Remote connections are allowed. Project data is never sent to the ad manager.'}
          onClick={() => mutate('Toggle local-only mode', (draft) => {
            draft.settings.localOnly = !draft.settings.localOnly;
            draft.privacyLedger.push({ id: crypto.randomUUID(), timestamp: new Date().toISOString(), category: 'local', purpose: draft.settings.localOnly ? 'Local-only mode enabled' : 'Remote connections enabled by user' });
          })}><ShieldCheck size={13} />{project.settings.localOnly ? 'Local only' : 'Remote allowed'}</button>
        <button {...triggerProps('profile')} type="button" className={`profile-button ${active === 'profile' ? 'active' : ''}`} aria-label="User profile — log in or join" title="User profile — log in or join">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="8" r="3.5" /><path d="M5 20v-1a7 7 0 0 1 14 0v1" /></svg>
        </button>
      </div>
    </header>
    <div className="network-header">
      <nav className="network-menu" role="menubar" aria-label="Network menu" aria-orientation="horizontal">
        {NETWORK_MENUS.map(({ label }) => {
          const id = menuId(label);
          return <button {...triggerProps(id)} type="button" key={id} role="menuitem" tabIndex={tabStop === id ? 0 : -1}
            className={active === id ? 'active' : ''}
            onPointerEnter={(event) => {
              if (event.pointerType === 'mouse' && active && active !== 'profile' && active !== id) {
                focusTrigger(id); open(id); hoverOpened.current = true;
              }
            }}>{label}</button>;
        })}
      </nav>
    </div>
    {active && createPortal(<div ref={popover} id={`header-menu-${active}`} className={`header-popover ${active === 'profile' ? 'profile-popover' : 'network-popover'}`}
      role="menu" aria-labelledby={`header-trigger-${active}`} onKeyDown={(event) => onKeys(event, active, true)}>
      {active === 'profile' ? <>
        <div className="profile-summary" role="presentation"><strong>Your account</strong><span>Connect with ZenID</span><small>Account links open PicPlayer in a new tab. Your NumberNode project stays here.</small></div>
        <div className="menu-separator" role="separator" />
        <a role="menuitem" tabIndex={-1} href={safeWebLink(PROFILE_LINKS.login, 'https://picplayer.com/auth/login')} target="_blank" rel="noopener noreferrer" onClick={() => close(true)}>Log in</a>
        <a role="menuitem" tabIndex={-1} className="profile-join" href={safeWebLink(PROFILE_LINKS.join, 'https://picplayer.com/auth/join')} target="_blank" rel="noopener noreferrer" onClick={() => close(true)}>Join</a>
        <div className="menu-separator" role="separator" />
        <a role="menuitem" tabIndex={-1} className="profile-legal-link" href={safeWebLink(PROFILE_LINKS.terms, 'https://picplayer.com/terms.html')} target="_blank" rel="noopener noreferrer" title="PicPlayer network terms" onClick={() => close(true)}>Terms of Service</a>
        <a role="menuitem" tabIndex={-1} className="profile-legal-link" href={safeWebLink(PROFILE_LINKS.privacy, '/privacy.html')} target="_blank" rel="noopener noreferrer" onClick={() => close(true)}>Privacy Policy</a>
      </> : currentMenu?.groups.map((group, groupIndex) => <div key={groupIndex} role="presentation">
        {groupIndex > 0 && <div className="menu-separator" role="separator" />}
        {group.map((name) => name === 'NumberNode' ?
          <button type="button" role="menuitem" tabIndex={-1} key={name} aria-current="page" onClick={() => close(true)}><span>{name}</span><small className="current-app-badge">Current app</small></button> :
          <a role="menuitem" tabIndex={-1} key={name} href={APP_URLS[name]} target="_blank" rel="noopener noreferrer" onClick={() => close(true)}>{name}</a>)}
      </div>)}
    </div>, document.body)}
  </>;
}
